import { Asset } from 'contentful'

import { IReducedAsset } from '../../types/reducedTypes'
import { ensureLinkHasProtocol } from '../ensureLinkHasProtocol'
import { ContentfulImageFormat } from '../nextImageLoader'

interface ContentfulImageSrcOptions {
  format?: ContentfulImageFormat
  width?: number
  height?: number
  quality?: number
}

export const getContentfulImageSrc = (
  src: string,
  options: ContentfulImageSrcOptions = {},
) => {
  const { format = 'webp', quality = 100, width, height } = options

  const url = new URL(ensureLinkHasProtocol(src))

  if (!src.endsWith('.svg')) {
    url.searchParams.set('fm', format)

    url.searchParams.set('q', String(quality))

    if (width) {
      url.searchParams.set('w', String(width))
    }

    if (height) {
      url.searchParams.set('h', String(height))
    }
  }

  return url.toString()
}

export const getContentfulImageSrcNewWidthAndHeight = (
  originalWidth: number,
  originalHeight: number,
  requestedWidth?: number,
  requestedHeight?: number,
) => {
  // If neither width nor height is specified, return original dimensions
  if (!requestedWidth && !requestedHeight) {
    return { width: originalWidth, height: originalHeight }
  }

  // Default to original dimensions if one of them is not provided
  requestedWidth = requestedWidth || originalWidth
  requestedHeight = requestedHeight || originalHeight

  // Calculate aspect ratios
  const originalAspectRatio = originalWidth / originalHeight
  const requestedAspectRatio = requestedWidth / requestedHeight

  let newWidth, newHeight

  if (requestedAspectRatio > originalAspectRatio) {
    // Requested aspect ratio is wider than original, fit to height
    newHeight = requestedHeight
    newWidth = requestedHeight * originalAspectRatio
  } else {
    // Requested aspect ratio is narrower than original, fit to width
    newWidth = requestedWidth
    newHeight = requestedWidth / originalAspectRatio
  }

  return {
    width: Math.round(newWidth ?? originalWidth),
    height: Math.round(newHeight ?? originalHeight),
  }
}

/**
 * Get the background image URL from Contentful fields.
 *
 * @returns The URL of the background image or an empty string if not found.
 */
export const getBackgroundImageSrc = (
  image?: Asset,
  options: ContentfulImageSrcOptions = {},
): string => {
  if (!image?.fields?.file?.url) {
    return ''
  }

  return getContentfulImageSrc(image.fields?.file?.url, options)
}
export const getContentfulImageProps = (
  image?: Asset | IReducedAsset,
  options: ContentfulImageSrcOptions = {},
): { alt: string; src: string; width: number; height: number } => {
  if (!image) {
    return {
      alt: '',
      src: '',
      width: 0,
      height: 0,
    }
  }

  const originalWidth = image?.fields?.file?.details?.image?.width ?? 0
  const originalHeight = image?.fields?.file?.details?.image?.height ?? 0

  const { width, height } = getContentfulImageSrcNewWidthAndHeight(
    originalWidth,
    originalHeight,
    options.width,
    options.height,
  )

  return {
    alt: image?.fields?.description ?? '',
    src: getContentfulImageSrc(image?.fields?.file?.url ?? '', options),
    width,
    height,
  }
}
